import { BaseSensorWebView } from '../../base/view/BaseSensorWebView';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { SensorType } from '../../types/sensor-type';
import { RequiredValidator } from '../../../../ui/gzForm/validators/required.validator';
import { MaxLengthValidator } from '../../../../ui/gzForm/validators/max-length.validator';
import { EditIntegrationConstants } from '../../../editIntegration/EditIntegrationConstants';
import { RegexValidator } from '../../../../ui/gzForm/validators/regex.validator';
import _ from 'lodash';
import { Sensor } from '../../../common/types';
import { WEB_COMPONENT_GZ_LABELED_CONTENT, WEB_COMPONENT_GZ_TOOLTIP } from '../../../../webcomponents.constants';
import { htmlEncode } from '../../../../utils/escape.utils';

export class AzureCWSensorView extends BaseSensorWebView {
  private static SUBSCRIPTION_ID_KEY = 'subscriptionId';

  public formGroup: FormGroup;

  public dom: {
    $applicationID: GzFormFieldWebComponent;
    $tenantID: GzFormFieldWebComponent;
    $clientSecret: GzFormFieldWebComponent;
    $subscriptionID: GzFormFieldWebComponent;
  };

  static getRequirements(dependencies: any): string {
    return `
      <div class="sensor-requirement" sensor-type="${SensorType.AZURE_CW}">
        <gz-title class="sensor-title text-semibold" fontSize="14">${dependencies.translateFn(
          'Before you integrate Azure Cloud with GravityZone, make sure the following requirements are met:'
        )}
        </gz-title>
        <ol>
          <li>
            ${dependencies.translateFn('Your managed application is {register} in Microsoft Azure AD.', {
              register:
                '<gz-link type="underline" href="https://docs.microsoft.com/en-us/azure/active-directory/reports-monitoring/howto-configure-prerequisites-for-reporting-api#register-an-application"><span>' +
                dependencies.translateFn('registered') +
                '</span></gz-link>',
            })}
          </li>
          <li>
            ${dependencies.translateFn('{aSubscription} is available for use.', {
              aSubscription:
                '<gz-link type="underline" href="https://docs.microsoft.com/en-us/azure/cost-management-billing/manage/create-subscription"><span>' +
                dependencies.translateFn('A subscription') +
                '</span></gz-link>',
            })}
          </li>
          <li>
            ${dependencies.translateFn(
              'Both the application and the subscription are set up with a role which has the {microsoftInsightPermission} permission assigned to it.',
              {
                microsoftInsightPermission: 'Microsoft.Insights/eventtypes/values/Read',
              }
            )}
          </li>
          <li>
            ${dependencies.translateFn('{ClientSecret} is generated, unless you have one already.', {
              ClientSecret:
                '<gz-link type="underline" href="https://docs.microsoft.com/en-us/azure/active-directory/reports-monitoring/howto-configure-prerequisites-for-reporting-api#get-your-applications-client-secret"><span>' +
                dependencies.translateFn('Client secret') +
                '</span></gz-link>',
            })}
          </li>
        </ol>
        <p>${dependencies.translateFn('For a complete Azure Cloud sensor integration guide, refer to {detailLink}.', {
          detailLink:
            '<gz-link type="underline" href="https://www.bitdefender.com/business/support/en/77211-452347-the-azure-cloud-sensor.html"><span>' +
            dependencies.translateFn('this KB article') +
            '</span></gz-link>',
        })}</p>
      </div>
      `;
  }

  public static getSensorSpecifics(sensor: Sensor, dependencies: any) {
    let result = ``;
    if (sensor.specifics.subscriptionId) {
      result += AzureCWSensorView.buildSensorDetailInfo(
        AzureCWSensorView.SUBSCRIPTION_ID_KEY,
        sensor.specifics.subscriptionId,
        dependencies
      );
    }

    return result;
  }

  private static buildSensorDetailInfo(key: string, value: string, dependencies: any): string {
    if (key === AzureCWSensorView.SUBSCRIPTION_ID_KEY) {
      const label = dependencies.translateFn('ID');
      return `<${WEB_COMPONENT_GZ_LABELED_CONTENT} label="${label}" hasColon="true">
                <${WEB_COMPONENT_GZ_TOOLTIP} class="tooltip-subscriptionId" tooltipText="${htmlEncode(value)}">
                    ${value}
                </${WEB_COMPONENT_GZ_TOOLTIP}>
            </${WEB_COMPONENT_GZ_LABELED_CONTENT}>`;
    }

    return '';
  }

  getDomElements(): void {
    super.getDomElements();
    this.dom = {
      $applicationID: this.shadow.querySelector('#azure_cw_application_id'),
      $tenantID: this.shadow.querySelector('#azure_cw_tenant_id'),
      $clientSecret: this.shadow.querySelector('#azure_cw_client_secret'),
      $subscriptionID: this.shadow.querySelector('#azure_cw_subscription_id'),
      ...this.baseDom,
    };

    this.getTranslations();
  }

  protected createFormGroup() {
    this.dom.$applicationID.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(
        EditIntegrationConstants.ApplicationId.MaxLength,
        this.dependencies.translateFn('The application ID must have a maximum of {maxLength} characters.', {
          maxLength: EditIntegrationConstants.ApplicationId.MaxLength,
        })
      ),
      RegexValidator(/^\S*$/, this.dependencies.translateFn('The application ID cannot contain spaces')),
    ];

    this.dom.$tenantID.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(
        EditIntegrationConstants.TenantId.MaxLength,
        this.dependencies.translateFn('The tenant ID must have a maximum of {maxLength} characters.', {
          maxLength: EditIntegrationConstants.TenantId.MaxLength,
        })
      ),
      RegexValidator(/^\S*$/, this.dependencies.translateFn('The tenant ID cannot contain spaces')),
    ];

    this.dom.$clientSecret.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(
        EditIntegrationConstants.ClientSecret.MaxLength,
        this.dependencies.translateFn('The Client Secret must have maximum {maxLength} characters.', {
          maxLength: EditIntegrationConstants.ClientSecret.MaxLength,
        })
      ),
      RegexValidator(/^\S*$/, this.dependencies.translateFn('The Client Secret cannot contain spaces')),
    ];

    this.dom.$subscriptionID.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(
        EditIntegrationConstants.ClientSecret.MaxLength,
        this.dependencies.translateFn('The subscription ID must have a maximum of {maxLength} characters.', {
          maxLength: EditIntegrationConstants.ClientSecret.MaxLength,
        })
      ),
      RegexValidator(/^\S*$/, this.dependencies.translateFn('The Subscription ID cannot contain spaces')),
    ];

    this.formGroup = new FormGroup({
      application_id: this.dom.$applicationID,
      tenant_id: this.dom.$tenantID,
      client_secret: this.dom.$clientSecret,
      subscription_id: this.dom.$subscriptionID,
    });
  }

  setTranslatedStrings(): void {
    this.dom.$applicationID.label = this.dependencies.translateFn('Application ID');
    this.dom.$tenantID.label = this.dependencies.translateFn('Tenant');
    this.dom.$clientSecret.label = this.dependencies.translateFn('Client Secret');
    this.dom.$subscriptionID.label = this.dependencies.translateFn('Subscription ID');
    this.setBaseTranslatedStrings(SensorType.AZURE_CW);
  }

  protected async setViewData(viewData) {
    super.setViewData(viewData);
    if (_.get(viewData, ['response', '_id'], false)) {
      this.formGroup.value = {
        application_id: _.get(viewData, ['response', 'configuration', 'application_id'], ''),
        tenant_id: _.get(viewData, ['response', 'configuration', 'tenant_id'], ''),
        client_secret: _.get(viewData, ['response', 'configuration', 'client_secret'], ''),
        subscription_id: _.get(viewData, ['response', 'configuration', 'subscription_id'], ''),
      };
    }
  }

  public setEnabledAllFields(isEnabled: boolean): void {
    this.baseDom.$sensorSelectWrapper.disabled = !isEnabled;
    if (isEnabled) {
      this.formGroup.setChildrenAttribute('disabled', '');
    } else {
      this.formGroup.setChildrenAttribute('disabled', 'disable');
    }
  }
}
